/**
 *
 * ListEmployees
 *
 */

import { useMemo } from 'react';
import { Box, Flex, Spinner, SystemStyleObject, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { EmptyPageIcon } from 'assets/icons';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import Select, { IOption } from 'app/components/Select';
import TableGroup from 'app/components/TableGroup';
import { useDebounce } from 'hooks/useDebounce';
import { useSession } from 'hooks/useSession';
import { useCustomToast } from 'hooks/useToast';
import { getAllCompanies, IResponseCompanies } from 'services/http/company';
import { getAllEmployeesIndicators, getListEmployeesNoPaginate, ICompaniesAllEmployees } from 'services/http/employee';
import { getHistoryYears } from 'services/http/flow';
import { IResponseListEmployees } from 'services/http/user';
import { MONTHS } from 'utils/constants';
import { handleDisableSelect } from 'utils/handlePermissions';
import { formatDecimalNumber, formatNumbers } from 'utils/Numbers';
import { parseErrors, ResponseErrors } from 'utils/parseErrors';
import { parsedOptionArray } from 'utils/parseOptionArray';

interface IFilterEmplyoeesForm {
	company?: IOption[] | null;
	employee?: IOption[] | null;
	year?: IOption | null;
	month?: IOption | null;
}

const ListEmployees = () => {
	const styles: Record<string, SystemStyleObject> = {
		container: {
			padding: '80px 58px',
			flexDirection: 'column',
			w: '100%',
			maxWidth: '112.75rem',
			margin: '0 auto',
			mt: '3.125rem',
			mb: '3.125rem',
			gap: '2.5rem',
			alignItems: { base: 'center', xl: 'unset' },
		},
		title: {
			fontSize: '1.875rem',
			fontWeight: '600',
			paddingTop: '40px',
			w: '100%',
		},
		filter: {
			gap: '2rem',
			w: '100%',
			flexDir: {
				base: 'column',
				xl: 'row',
			},
		},
		boxMultiSelect: {
			width: { base: '100%', xl: '15.25rem' },
		},
		boxSelect: {
			width: { base: '100%', xl: '12.5rem' },
		},
		table: {
			mt: '1.625rem',
			mb: '2.5rem',
			maxH: 'calc(100vh - 16rem)',
		},
		spinnerBox: {
			w: '100%',
			h: '50vh',
			alignItems: 'center',
			justifyContent: 'center',
			flexDir: 'column',
			gap: '2rem',
		},
	};

	const {
		session: { user },
	} = useSession();

	const { companiesUser } = useMemo(() => {
		return {
			accessProfile: parsedOptionArray<string>(user.accessProfile),
			companiesUser: user?.company,
			departmentUser: user?.department,
		};
	}, [user]);

	const { addToast } = useCustomToast();

	const isSelectDisabled = handleDisableSelect(user);

	const { data: companies } = useQuery<IResponseCompanies, AxiosError<ResponseErrors>, IResponseCompanies>(
		['companies'],
		() => getAllCompanies(),
		{
			onSuccess: data => {
				const currentUserCompanies =
					data.companiesObjects?.filter(cpn => {
						return companiesUser.some(id => Number(id) === Number(cpn?.id));
					}) || [];

				const multiSelectOptions = currentUserCompanies.map(company => ({
					label: company.company,
					value: company.id,
				}));

				setValue('company', multiSelectOptions);
			},
			onError: errors => addToast({ type: 'error', description: parseErrors(errors?.response?.data) }),
		},
	);

	const { control, handleSubmit, watch, setValue } = useForm<IFilterEmplyoeesForm>({});

	const formValues = watch();

	const {
		data: listEmployees,
		refetch: refetchListEmployees,
		isLoading: isLoadingListEmployees,
		isRefetching: isRefetchingListEmployees,
	} = useQuery<ICompaniesAllEmployees[], AxiosError<ResponseErrors>, ICompaniesAllEmployees[]>(
		['listEmployees', formValues?.company, formValues?.month, formValues?.year, formValues?.employee],
		() =>
			getAllEmployeesIndicators({
				companies_id: formValues?.company?.map(cpn => String(cpn?.value)),
				sellers_id: formValues?.employee?.map(sel => String(sel?.value)),
				month: String(formValues?.month?.value),
				year: String(formValues?.year?.value),
			}),
		{
			onError: errors => addToast({ type: 'error', description: parseErrors(errors?.response?.data) }),
			enabled: Boolean(
				formValues?.company?.length &&
					formValues?.employee?.length &&
					formValues?.month?.value &&
					formValues?.year?.value,
			),
		},
	);

	const { data: listEmployeesNoPaginated } = useQuery<IResponseListEmployees, AxiosError<ResponseErrors>>(
		['listEmployeesNoPaginated', formValues?.company],
		() => {
			const companiesIds = formValues?.company?.map((company: IOption) => Number(company.value)) || [];

			return getListEmployeesNoPaginate({
				companies: companiesIds.length > 0 ? companiesIds : undefined,
			});
		},
		{
			select: data => ({
				...data,
				results:
					data.results?.filter(
						(vendedor, index, self) => index === self.findIndex(v => v.username === vendedor.username),
					) || [],
			}),
			onError: errors =>
				addToast({
					type: 'error',
					description: parseErrors(errors?.response?.data),
				}),
			enabled: !!formValues?.company,
		},
	);

	const { data: years } = useQuery<number[], AxiosError<ResponseErrors>, number[]>(
		['historyYears'],
		() => getHistoryYears(),
		{
			onError: errors =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
		},
	);

	const processUnitData = (unitData: ICompaniesAllEmployees) => {
		if (!unitData || !Array.isArray(unitData.employees)) {
			return {
				unitName: unitData?.company || 'Sem nome',
				tableData: {
					columns: [{ label: 'INDICADORES', key: 'indicadores' }],
					data: [],
				},
			};
		}

		const vendedores = unitData.employees.reduce((acc: string[], employee: any) => {
			const vendedor = Object.keys(employee)[0];
			return vendedor ? [...acc, vendedor] : acc;
		}, []);

		const roundAverage = (isInteger: boolean, data: number) => (isInteger ? Math.round(data) : data);

		const generateTableData = (key: string, formatNumber = false, formatCurrency = false) => {
			const rowData: any = {};

			vendedores.forEach((vendedor: string) => {
				const funcionario = unitData.employees.find((f: any) => f && f[vendedor]);

				if (!funcionario) {
					rowData[vendedor] = '-';
					return;
				}

				const indicadores = funcionario[vendedor];
				const indicador = Array.isArray(indicadores) ? indicadores.find((item: any) => item?.indicator === key) : null;

				if (indicador?.average !== undefined) {
					const isInteger = !formatNumber && !formatCurrency;
					let average = Number(indicador.average);

					if (['tx_evaluation', 'tx_purchase', 'tx_capture', 'conversion'].includes(key)) {
						average = average * 100;
					}

					average = roundAverage(isInteger, average);

					rowData[vendedor] = formatNumber
						? formatDecimalNumber(average, '%')
						: formatCurrency
						? formatNumbers(average, true)
						: formatNumbers(average, false, isInteger ? 0 : 2);
				} else {
					rowData[vendedor] = '-';
				}
			});

			return rowData;
		};

		return {
			unitName: unitData.company,
			tableData: {
				columns: [
					{
						label: 'INDICADORES',
						key: 'indicadores',
					},
					...vendedores?.map((vendedor: any) => ({
						label: vendedor,
						key: vendedor,
						columnCustomStyle: {
							textAlign: 'center',
						},
					})),
				],
				data: [
					{
						showHeaderGroup: false,
						children: [
							{
								indicadores: 'Fluxo de Loja (Passantes)',
								...generateTableData('fluxo_loja_passante'),
							},
							{
								indicadores: 'Fluxo de Ligações Recebidas',
								...generateTableData('fluxo_ligacoes'),
							},
							{
								indicadores: 'Comparecimento Ligações',
								...generateTableData('comparecimento_ligacoes'),
							},
							{
								indicadores: 'Fluxo Loja Vendedor',
								...generateTableData('fluxo_loja_vendedor'),
							},
							{
								indicadores: 'Ligações Recebidas',
								...generateTableData('ligacoes_recebidas'),
							},
							{
								indicadores: 'Comparecimento',
								...generateTableData('comparecimento'),
							},
							{
								indicadores: 'Conversão',
								...generateTableData('conversao'),
							},
						],
					},

					{
						showHeaderGroup: true,
						indicadores: 'VENDAS TOTAL',
						...generateTableData('conversao'),
						children: [
							{
								indicadores: 'Faturamento',
								...generateTableData('billings', false, true),
							},
							{
								indicadores: 'Ticket Médio',
								...generateTableData('average_ticket', false, true),
							},
							{
								indicadores: 'Despesas com Cortesias',
								...generateTableData('complimentary_expenses', false, true),
							},
							{
								indicadores: '% M.B',
								...generateTableData('percentage_mb', true),
							},
						],
					},

					{
						showHeaderGroup: true,
						indicadores: 'FINANCIAMENTO',
						...generateTableData('financing', false, true),
						children: [
							{
								indicadores: 'Contratos Financiamento',
								...generateTableData('financing_contracts'),
							},
							{
								indicadores: 'Penetracion Financiamento',
								...generateTableData('financing_penetration', true),
							},
							{
								indicadores: 'Evasão financiamento',
								...generateTableData('financing_evasion', true),
							},
							{
								indicadores: 'Penetration Valor Financiado',
								...generateTableData('financing_value_penetration', true),
							},
							{
								indicadores: 'Retorno por Carro',
								...generateTableData('car_return', false, true),
							},
							{
								indicadores: 'Rentabilidade Financiamento',
								...generateTableData('financing_profitability', true),
							},
						],
					},

					{
						showHeaderGroup: true,
						indicadores: 'TCCB',
						...generateTableData('tccb', true),
						children: [
							{
								indicadores: 'TCCB por Contrato',
								...generateTableData('tccb_by_contract', false, true),
							},
							{
								indicadores: 'TCCB por Carro',
								...generateTableData('tccb_by_car', false, true),
							},
						],
					},

					{
						showHeaderGroup: true,
						indicadores: 'EMPLACAMENTO',
						...generateTableData('placement', true),
						children: [
							{
								indicadores: 'Emplacamento por carro',
								...generateTableData('placement_by_car', false, true),
							},
						],
					},

					{
						showHeaderGroup: true,
						indicadores: 'CAPTAÇÃO',
						...generateTableData('capture'),
						children: [
							{
								indicadores: 'Avaliação',
								...generateTableData('evaluation'),
							},
							{
								indicadores: 'Taxa avaliação',
								...generateTableData('tx_evaluation', true),
							},
							{
								indicadores: 'Taxa compra',
								...generateTableData('tx_buy', true),
							},
							{
								indicadores: 'Taxa captura',
								...generateTableData('tx_capture', true),
							},
						],
					},

					{
						showHeaderGroup: true,
						indicadores: 'MARGEM DE CONTRIBUIÇÃO',
						...generateTableData('contribution_margin', false, true),
						children: [
							{
								indicadores: 'Lucro Bruto Total',
								...generateTableData('total_gross_profit', false, true),
							},
							{
								indicadores: '(%) Lucro Bruto s/Faturamento',
								...generateTableData('gross_profitwithout_revenue', true),
							},
						],
					},

					{
						showHeaderGroup: true,
						indicadores: 'ACESSÓRIOS',
						...generateTableData('accessories', false, true),
						children: [
							{
								indicadores: 'Ticket Médio Acessorios',
								...generateTableData('medium_accessory_ticket', false, true),
							},
							{
								indicadores: 'TCCB',
								...generateTableData('tccb', true),
							},
						],
					},
					{
						showHeaderGroup: true,
						indicadores: 'SALÁRIO',
						...generateTableData('salary', false, true),
						children: [
							{
								indicadores: 'Comissão sobre venda',
								...generateTableData('commission_on_sale', true),
							},
							{
								indicadores: 'Comissão sobre margem',
								...generateTableData('comission_on_margin', true),
							},
							{
								indicadores: 'Comissão financiamento',
								...generateTableData('financing_commission', true),
							},
							{
								indicadores: 'Comissão despachante',
								...generateTableData('forwarding_commission', true),
							},

							{
								indicadores: 'Comissão TCCB',
								...generateTableData('tccb_commission', true),
							},
							{
								indicadores: 'Valor venda',
								...generateTableData('sale_value'),
							},
							{
								indicadores: 'Quantidade venda',
								...generateTableData('quantity_sale', false, true),
							},
							{
								indicadores: 'Margem total',
								...generateTableData('total_margin', false, true),
							},
							{
								indicadores: 'Receita financiamento',
								...generateTableData('financing_income', false, true),
							},
							{
								indicadores: 'Receita despachante',
								...generateTableData('dispatcher_revenue', false, true),
							},
							{
								indicadores: 'Receita TCCB',
								...generateTableData('revenue_tccb', false, true),
							},
						],
					},
				],
			},
		};
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const generateAllTables = (jsonData?: ICompaniesAllEmployees[]) => {
		if (!jsonData) return [];

		const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];

		return dataArray.map(unitData => processUnitData(unitData));
	};

	const allTables = useMemo(() => {
		return generateAllTables(listEmployees);
	}, [generateAllTables, listEmployees]);

	const onFilter = () => {
		handleSubmit(onSubmit)();
	};

	const debounced = useDebounce(() => {
		if (formValues.company && formValues.employee && formValues.year && formValues.month) {
			onFilter();
		}
	});

	const onSubmit = () => refetchListEmployees();

	const hasEmptyOrInvalid = Object.values(formValues).some(val => val == null || val === '');

	return (
		<>
			<Box sx={styles.container}>
				<Flex sx={styles.filter} as="form" onSubmit={handleSubmit(onSubmit)}>
					<Box sx={styles?.boxMultiSelect}>
						<Controller
							name="company"
							control={control}
							render={({ field: { onChange, value } }) => (
								<Select
									label="Unidade"
									placeholder="Unidade"
									onChange={option => {
										onChange(option);
										debounced();
									}}
									value={value}
									options={parsedOptionArray(companies?.companiesObjects, 'company', 'id') || []}
									dataTestId="select--company"
									variant="medium"
									isDisabled={isSelectDisabled}
									isMulti
								/>
							)}
						/>
					</Box>
					<Box sx={styles?.boxMultiSelect}>
						<Controller
							name="employee"
							control={control}
							render={({ field: { onChange, value } }) => (
								<Select
									isDisabled={false}
									label="Vendedor"
									placeholder={'Vendedor'}
									onChange={option => {
										onChange(option);
										debounced();
									}}
									value={value}
									options={parsedOptionArray(listEmployeesNoPaginated?.results, 'username', 'id') || []}
									dataTestId="select--employee"
									variant="medium"
									isMulti
								/>
							)}
						/>
					</Box>
					<Box sx={styles?.boxSelect}>
						<Controller
							name="year"
							control={control}
							render={({ field: { onChange, value } }) => (
								<Select
									label="Ano de referência"
									placeholder="Ano"
									onChange={option => {
										onChange(option);
										debounced();
									}}
									value={value}
									options={parsedOptionArray(years)}
									dataTestId="select--year"
									variant="medium"
									isDisabled={false}
								/>
							)}
						/>
					</Box>
					<Box sx={styles?.boxSelect}>
						<Controller
							name="month"
							control={control}
							render={({ field: { onChange, value } }) => (
								<Select
									label="Mês de referência"
									placeholder="Mês"
									onChange={option => {
										onChange(option);
										debounced();
									}}
									value={value}
									options={MONTHS}
									dataTestId="select--month"
									variant="medium"
									isDisabled={false}
								/>
							)}
						/>
					</Box>
				</Flex>

				{hasEmptyOrInvalid && (
					<Flex sx={styles.spinnerBox}>
						<EmptyPageIcon />
						<Text fontSize={'30px'}>Selecione os filtros para acessar o conteúdo.</Text>
					</Flex>
				)}

				{!hasEmptyOrInvalid && !isLoadingListEmployees && !isRefetchingListEmployees && allTables?.length === 0 && (
					<Flex sx={styles.spinnerBox}>
						<EmptyPageIcon />
						<Text fontSize={'30px'}>Nenhum resultado encontrado com os filtros selecionados.</Text>
					</Flex>
				)}

				{isLoadingListEmployees && isRefetchingListEmployees ? (
					<Flex sx={styles.spinnerBox}>
						<Spinner />
					</Flex>
				) : (
					allTables?.map(unitTable => (
						<Flex flexDir={'column'} key={unitTable?.unitName}>
							<Text sx={styles.title}>{unitTable?.unitName}</Text>
							<TableGroup striped data={unitTable.tableData.data} columns={unitTable?.tableData?.columns} />
						</Flex>
					))
				)}
			</Box>
		</>
	);
};

export default ListEmployees;
